export type CentralDbUrls = {
  origin: string;
  apiBaseUrl: string;
};

const getEnv = (k: string): string | undefined => {
  try {
    return (import.meta.env as any)?.[k] as string | undefined;
  } catch {
    return undefined;
  }
};

export const getCentralDbUrls = (): CentralDbUrls => {
  const configured =
    (getEnv('VITE_CENTRAL_DB_API_URL') as string | undefined) ||
    (getEnv('VITE_API_BASE_URL') as string | undefined) ||
    'http://localhost:4000/api/v1';

  const normalized = String(configured).replace(/\/+$/, '');
  const origin = normalized.replace(/\/api\/v\d+$/i, '');
  const apiBaseUrl = /\/api\/v\d+$/i.test(normalized) ? normalized : `${origin}/api/v1`;
  return { origin, apiBaseUrl };
};

export type CentralDbRequestOptions = {
  method: string;
  path: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: any;
  token?: string | null;
  apiKey?: string | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export type CentralDbResponse = {
  ok: boolean;
  status: number;
  headers: Record<string, string>;
  data: any;
};

const toQueryString = (query?: CentralDbRequestOptions['query']): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const resolveUrl = (urls: CentralDbUrls, path: string, query?: CentralDbRequestOptions['query']): string => {
  const p = path.startsWith('/') ? path : `/${path}`;
  const isAlreadyPrefixed = p.startsWith('/api/');
  const base = isAlreadyPrefixed ? urls.origin : urls.apiBaseUrl;
  return `${base}${p}${toQueryString(query)}`;
};

export const centralDbRequest = async (options: CentralDbRequestOptions): Promise<CentralDbResponse> => {
  const urls = getCentralDbUrls();
  const url = resolveUrl(urls, options.path, options.query);

  const headers: Record<string, string> = {
    'X-App-Id': 'administration-frontend',
    ...(options.headers || {}),
  };

  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }
  const apiKey = options.apiKey ?? (getEnv('VITE_CENTRAL_DB_API_KEY') as string | undefined) ?? undefined;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }

  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    body = headers['Content-Type'].includes('application/json') ? JSON.stringify(options.body) : String(options.body);
  }

  const res = await fetch(url, {
    method: options.method,
    headers,
    body,
    signal: options.signal,
  });

  const outHeaders: Record<string, string> = {};
  res.headers.forEach((value, key) => {
    outHeaders[key.toLowerCase()] = value;
  });

  const contentType = outHeaders['content-type'] || '';
  let data: any = null;
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    data = await res.text();
  }

  return {
    ok: res.ok,
    status: res.status,
    headers: outHeaders,
    data,
  };
};
